/**
 * BOM review-table helpers. Flag config, batch/file summaries, and the adapter
 * from extraction output to the review-table line shape.
 *
 * (Filename is legacy from the prototype's mock-data phase. The mocks are gone
 * but consumers still import from this path.)
 */

export const bomLineFlagConfig = {
  reported: {
    key: "reported",
    colorKey: "secondary",
    label: "Reported",
    tooltip: "Weight came directly from the BOM."
  },
  assumption: {
    key: "assumption",
    colorKey: "warning",
    label: "Assumption data",
    tooltip:
      "No weight on the BOM — filled from a typical value for this component type."
  },
  insufficient: {
    key: "insufficient",
    colorKey: "error",
    label: "Insufficient data",
    tooltip:
      "No weight and no assumption available, or the material is not in our emissions database."
  }
};

export const summarizeBatch = (batch) => {
  const allLines = batch.files.flatMap(({ lines = [], packaging = [] }) => [
    ...lines,
    ...packaging
  ]);

  const countByFlag = (flag) =>
    allLines.filter((line) => line.flag === flag).length;

  return {
    productCount: batch.files.length,
    lineCount: allLines.length,
    reportedCount: countByFlag("reported"),
    assumptionCount: countByFlag("assumption"),
    insufficientCount: countByFlag("insufficient")
  };
};

export const summarizeFile = (file) => {
  const allLines = [...(file.lines || []), ...(file.packaging || [])];
  const countByFlag = (flag) =>
    allLines.filter((line) => line.flag === flag).length;

  return {
    lineCount: allLines.length,
    reportedCount: countByFlag("reported"),
    assumptionCount: countByFlag("assumption"),
    insufficientCount: countByFlag("insufficient")
  };
};

const stripFileNoise = (filename) =>
  filename
    .replace(/\.[^.]+$/, "")
    .replace(/[_\s-]*carbon data/i, "")
    .replace(/\s*copy$/i, "")
    .trim();

const looksLikeSku = (token) => /^[A-Za-z]{0,3}-?\d{2,}/.test(token);

const parseFilenameToProduct = (filename) => {
  const base = stripFileNoise(filename);
  const [firstToken, ...rest] = base.split(/[_\s]+/);

  if (looksLikeSku(firstToken) && rest.length) {
    return { sku: firstToken, name: rest.join(" ") };
  }

  return { sku: base, name: base };
};

/**
 * Adapt a parsed extraction (extractionEngine.simulateExtraction output) into
 * the review-table "file" shape so review/import can render it consistently.
 * Honors any product-level fields the user confirmed in the mapper.
 */
export const buildFileFromExtraction = (extraction) => {
  const filenameDerived = parseFilenameToProduct(extraction.fileName);
  const extracted = extraction.product || {};

  const toLine = (sectionName, material) => ({
    rawName: material.rawName,
    archetype: sectionName,
    section: sectionName,
    rawComposition: material.rawName,
    materialType: material.materialType,
    quantity: 1,
    weightGrams: material.weight ?? null,
    percent: material.percent ?? null,
    weightSource:
      material.weight != null
        ? "reported"
        : material.percent != null
        ? "percentage"
        : material.flag === "insufficient"
        ? "missing"
        : "assumption",
    flag: material.flag
  });

  const lines = (extraction.components || []).flatMap((component) =>
    component.materials.map((material) => toLine(component.name, material))
  );

  return {
    sourceFile: extraction.fileName,
    detectedTemplate: extraction.layout,
    parseConfidence: lines.length ? 0.92 : 0.4,
    status: lines.length ? "needs-review" : "low-confidence",
    product: {
      sku: extracted.sku || filenameDerived.sku,
      name: extracted.name || extracted.sku || filenameDerived.name,
      category: extracted.category || "apparel",
      subcategory: extracted.subcategory || null,
      countryOfOrigin: extracted.countryOfOrigin || null,
      cityOfOrigin: extracted.cityOfOrigin || null,
      productWeightGrams: extracted.productWeightGrams ?? null,
      packagingWeightGrams: extracted.packagingWeightGrams ?? null,
      totalWeightGrams: extracted.totalWeightGrams ?? null
    },
    lines,
    packaging: [],
    warnings: lines.length
      ? []
      : ["No materials could be read from this file's layout."]
  };
};
